import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http'
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationToastComponent } from '../components/notification-toast/notification-toast.component';
import { environment } from 'app/environment';
import { SERVICE_CONSTANTS } from './service.constants';


export enum NotificationPermissionState {
  GRANTED = 'granted',
  DENIED = 'denied',
  DEFAULT = 'default',
  UNSUPPORTED = 'unsupported'
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  deviceId: string;
  userAgent: string;
}

export interface NotificationConfig {
  vapidPublicKey?: string;
  apiEndpoint?: string;
}

export interface SubscriptionCheckResponse {
  subscribed: boolean;
  subscription?: string;
}

/**
 * Service Worker and Push Notification Service
 * Manages service worker integration and push notification functionality
 *
 * Note: Service Worker is registered in main.ts for all users
 * This service consumes the existing registration
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private baseApiUrl = environment.baseApiUrl

  private readonly _isSupported = this.isNotificationSupported();
  private permissionStateSubject = new BehaviorSubject<NotificationPermissionState>(
    this.getCurrentPermissionState()
  );
  private subscriptionStateSubject = new BehaviorSubject<boolean>(false);
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  // Configuration for push notifications (SW path is handled in main.ts)
  private config: NotificationConfig = {
    vapidPublicKey: environment.VAPID_PUBLIC_KEY,
    apiEndpoint: environment.VAPID_API_ENDPOINT,
  };

  private snackBar = inject(MatSnackBar);

  constructor(
    private http: HttpClient,
    private notificationsService?: NotificationsService
  ) {
    this.initializeService();
  }

  // Public observables
  get permissionState$(): Observable<NotificationPermissionState> {
    return this.permissionStateSubject.asObservable();
  }

  get subscriptionState$(): Observable<boolean> {
    return this.subscriptionStateSubject.asObservable();
  }

  get isSupported(): boolean {
    return this._isSupported;
  }

  private async initializeService(): Promise<void> {
    try {
      if (this.isNotificationSupported()) {
        await this.getServiceWorkerRegistration();
        // Don't check subscription status during initialization
        // This will be called explicitly when needed (e.g., when notification settings tab is opened)
        // await this.checkExistingSubscription();
      }
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
    }
  }

  /**
   * Refresh the subscription status - useful when component loads or user auth state changes
   */
  async refreshSubscriptionStatus(): Promise<void> {
    try {
      if (this.isNotificationSupported()) {
        await this.checkExistingSubscription();
      }
    } catch (error) {
      console.error('Failed to refresh subscription status:', error);
    }
  }

  private isNotificationSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  private getCurrentPermissionState(): NotificationPermissionState {
    if (!this.isNotificationSupported()) {
      return NotificationPermissionState.UNSUPPORTED;
    }

    const permission = Notification.permission;
    switch (permission) {
      case 'granted':
        return NotificationPermissionState.GRANTED;
      case 'denied':
        return NotificationPermissionState.DENIED;
      case 'default':
        return NotificationPermissionState.DEFAULT;
      default:
        return NotificationPermissionState.DEFAULT;
    }
  }

  /**
   * Get service worker registration (registered in main.ts)
   */
  private async getServiceWorkerRegistration(): Promise<void> {
    try {
      // Wait for service worker to be ready (registered in main.ts)
      const registration = await navigator.serviceWorker.ready;
      this.serviceWorkerRegistration = registration;
      console.log('NotificationService connected to Service Worker');

    } catch (error) {
      console.error('Failed to get service worker registration:', error);
      throw new Error('Failed to connect to service worker');
    }
  }

  private async checkExistingSubscription(): Promise<boolean> {
    console.log('🔍 Checking existing subscription...');

    try {
      if (!this.serviceWorkerRegistration) {
        console.log('❌ No service worker registration found');
        this.subscriptionStateSubject.next(false);
        return false;
      }

      const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      console.log('🔍 Browser subscription:', subscription ? 'EXISTS' : 'NOT FOUND');

      if (!subscription) {
        this.subscriptionStateSubject.next(false);
        return false;
      }

      try {
        console.log('🔍 Checking backend subscription status...');
        const subscriptionCheck = await firstValueFrom(
          this.http.post<SubscriptionCheckResponse>(
            this.baseApiUrl + this.config.apiEndpoint + '/check-subscription',
            { endpoint: subscription.endpoint }
          )
        );

        const isUserSubscribed = subscriptionCheck.subscribed;
        console.log('✅ Backend subscription check result:', isUserSubscribed);
        this.subscriptionStateSubject.next(isUserSubscribed);
        return isUserSubscribed;
      } catch (error) {
        // If backend check fails, fall back to browser subscription status
        console.warn('⚠️ Failed to check backend subscription status, using browser status:', error);
        const browserSubscribed = !!subscription;
        this.subscriptionStateSubject.next(browserSubscribed);
        return browserSubscribed;
      }
    } catch (error) {
      console.error('❌ Failed to get subscription status:', error);
      this.subscriptionStateSubject.next(false);
      return false;
    }
  }

  async requestPermission(): Promise<NotificationPermissionState> {
    if (!this.isNotificationSupported()) {
      const state = NotificationPermissionState.UNSUPPORTED;
      this.permissionStateSubject.next(state);
      throw new Error('Notifications are not supported in this browser');
    }

    try {
      const permission = await Notification.requestPermission();
      let state: NotificationPermissionState;

      switch (permission) {
        case 'granted':
          state = NotificationPermissionState.GRANTED;
          break;
        case 'denied':
          state = NotificationPermissionState.DENIED;
          break;
        default:
          state = NotificationPermissionState.DEFAULT;
      }

      this.permissionStateSubject.next(state);
      return state;
    } catch (error) {
      console.error('Failed to request permission:', error);
      throw new Error('Failed to request notification permission');
    }
  }

  async subscribe(): Promise<PushSubscriptionData> {
    const currentPermission = this.getCurrentPermissionState();

    if (currentPermission !== NotificationPermissionState.GRANTED) {
      throw new Error('Notification permission not granted');
    }

    if (!this.serviceWorkerRegistration) {
      throw new Error('Service worker not registered');
    }

    let subscription: PushSubscription | null = null;

    try {
      // Check if already subscribed
      subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();

      if (!subscription) {
        // No existing browser subscription - create new one
        const subscribeOptions: PushSubscriptionOptions = {
          userVisibleOnly: true,
          applicationServerKey: this.config.vapidPublicKey
            ? this.urlBase64ToUint8Array(this.config.vapidPublicKey).buffer as ArrayBuffer
            : undefined
        };

        subscription = await this.serviceWorkerRegistration.pushManager.subscribe(subscribeOptions);
      }

      // Check if current user is already subscribed to this endpoint
      const subscriptionCheck = await firstValueFrom(
        this.http.post<SubscriptionCheckResponse>(
          this.baseApiUrl + this.config.apiEndpoint + '/check-subscription',
          { endpoint: subscription.endpoint }
        )
      );

      if (!subscriptionCheck.subscribed) {
        // Current user is not subscribed to this endpoint - create new subscription record
        const subscriptionData: PushSubscriptionData = {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
            auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
          },
          deviceId: this.getDeviceId(),
          userAgent: navigator.userAgent,
        };

        await firstValueFrom(
          this.http.post(this.baseApiUrl + this.config.apiEndpoint + '/subscribe', subscriptionData)
        );

        console.log(`New subscription created for user on endpoint ${subscription.endpoint}`);
      } else {
        console.log(`User already subscribed to endpoint ${subscription.endpoint}`);
      }

      this.subscriptionStateSubject.next(true);
      return {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        },
        deviceId: this.getDeviceId(),
        userAgent: navigator.userAgent,
      };
    } catch (error) {
      subscription?.unsubscribe();
      console.error('Failed to subscribe to push notifications:', error);
      throw new Error('Failed to subscribe to push notifications');
    }
  }

  async unsubscribe(): Promise<void> {
    if (!this.serviceWorkerRegistration) {
      throw new Error('Service worker not registered');
    }

    try {
      const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();

      if (subscription) {
        // Remove subscription from server
        await firstValueFrom(this.http.post(this.baseApiUrl + this.config.apiEndpoint + '/unsubscribe', { endpoint: subscription.endpoint }))

        // Unsubscribe locally (don't unsubscribe to keep endpoint for other users)
        // await subscription.unsubscribe();
      }

      this.subscriptionStateSubject.next(false);
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw new Error('Failed to unsubscribe from push notifications');
    }
  }

  /**
   * Handle incoming push notification and display in-app
   */
  async handleIncomingNotification(notificationData: any): Promise<void> {
    try {
      // Show immediate toast
      this.snackBar.openFromComponent(NotificationToastComponent, {
        data: notificationData,
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: ['notification-snackbar']
      });

      // Create in-app notification
      const inAppNotification: Notification = {
        id: notificationData.id || this.generateNotificationId(),
        title: notificationData.title,
        description: notificationData.body,
        icon: notificationData.icon || 'heroicons_outline:bell',
        time: new Date().toISOString(),
        read: false,
        link: notificationData.url,
        useRouter: notificationData.useRouter || false
      };

      // Add to in-app notifications
      if (this.notificationsService) {
        this.notificationsService.create(inAppNotification).subscribe({
          next: () => console.log('In-app notification created'),
          error: (error) => console.error('Failed to create in-app notification:', error)
        });
      }

    } catch (error) {
      console.error('Failed to handle incoming notification:', error);
    }
  }

  /**
   * Send a test notification (local notification)
   */
  async sendTestNotification(): Promise<void> {
    const currentPermission = this.getCurrentPermissionState();

    if (currentPermission !== NotificationPermissionState.GRANTED) {
      throw new Error('Notification permission not granted');
    }

    try {
      const notification = new Notification('Test Notification', {
        body: 'This is a test notification from My Account app',
        icon: '/icons/notification-icon.png',
        tag: 'test-notification',
        requireInteraction: false
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw new Error('Failed to send test notification');
    }
  }

  /**
   * Send both push and in-app notification
   */
  async sendTestNotification2(notificationData: {
    title: string;
    body: string;
    icon?: string;
    url?: string;
    data?: any;
  }): Promise<void> {
    try {
      // Send push notification (browser notification)
      if (Notification.permission === 'granted') {
        const notification = new Notification(notificationData.title, {
          body: notificationData.body,
          icon: notificationData.icon || '/icons/notification-icon.png',
          data: notificationData.data
        });

        // Handle click
        notification.onclick = () => {
          if (notificationData.url) {
            window.open(notificationData.url, '_self');
          }
          notification.close();
        };
      }

      // Also add to in-app notifications
      await this.handleIncomingNotification(notificationData);

    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  }

  private generateNotificationId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `push_device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
      return deviceId;
  }

  /**
   * Convert VAPID key from URL-safe base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  setupServiceWorkerEventListener(): void {
    // Listen for custom push notification events dispatched from main.ts
    window.addEventListener(SERVICE_CONSTANTS.EVENTS.SW_PUSH_NOTIFICATION, (event: CustomEvent) => {
      this.handleIncomingNotification(event.detail);
    });
    console.log('Service Worker push notification event listener initialized in NotificationService');
  }
}
